import { useCallback, useMemo, useState } from 'react';
import { Grid, Typography } from 'antd';
import { WizardContainer } from '../../../components/wizard/Wizard';
import { useTranslation } from 'react-i18next';
import { WizardProvider } from '../../../components/wizard/useWizard';
import { AvailabilityAfterProgramStep } from './AvailabilityAfterProgramStep';
import { CharacterAndHabitsStep } from './CharacterAndHabitsStep';
import { DietaryHabitsAndHealthStep } from './DietaryHabitsAndHealthStep';
import { InspirationAndWillingnessStep } from './InspirationAndWillingnessStep';
import { PracticeStep } from './PracticeStep';
import { SupportStep } from './SupportStep';
import { UnderstandingOfHeartfulnessStep } from './UnderstandingOfHeartfulnessStep';
import {
	ApplicantInterviewProvider,
	useApplicantInterviewWizard,
} from './useApplicantInterviewWizard';
import { useInterviewLandingPage } from '../InterviewLandingPage/useInterviewLandingPage';
import {
	APPLICANT_CONTEXT,
	INTERVIEW_LANDING,
	VIEW_APPLICANT_INTERVIEW,
} from '../../../shared/Routes';
import Icon from '../../../components/ui-components/Icon';
import LeftArrow from '../../../assets/images/arrow-left.svg';
import { UserRole } from '../../../shared/VolunteerApplicationServiceTypes';
import { useLocation } from 'react-router-dom';
import useUserSession from '../../../hooks/useUserSession';
import { SELECTED_ROLES } from '../../../shared/Utils';

const { Text } = Typography;
const { useBreakpoint } = Grid;

export const ApplicantInterviewWizard = () => {
	const { onSubmitClick, application } = useApplicantInterviewWizard();
	const { t } = useTranslation();
	const { navigateToPage } = useInterviewLandingPage();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const { selectedRole } = useUserSession();
	const { pathname } = useLocation();

	const isRoleAllowed = SELECTED_ROLES.includes(selectedRole as UserRole);

	const isProposer = selectedRole === UserRole.PROPOSER;
	const isViewable = pathname.includes(VIEW_APPLICANT_INTERVIEW);
	const isProposerAndViewable = isProposer && isViewable;
	const currentWizard = 0;
	const stepOffset = 0;

	const wizards = useMemo(
		() => [
			{
				steps: [
					<InspirationAndWillingnessStep key={1} />,
					<PracticeStep key={2} />,
					<CharacterAndHabitsStep key={3} />,
					<AvailabilityAfterProgramStep key={4} />,
					<SupportStep key={5} />,
					<UnderstandingOfHeartfulnessStep key={6} />,
					<DietaryHabitsAndHealthStep key={7} />,
				],
				descriptions: [
					{
						title: t('inspirationAndWillingness'),
						description: t('primaryQualificationForATrainer') ?? '',
					},
					{
						title: t('practice'),
						description: t('trainerNeedsToBeIntensePractitioner') ?? '',
					},
					{
						title: t('characterAndHabits'),
						description: t('expectedToBeRoleModel') ?? '',
					},
					{
						title: t('availabilityAfterProgram'),
						description: t('expectedToBeRoleModel') ?? '',
					},
					{
						title: t('familySupport'),
						description: t('exploreIfConditionsAroundAreConducive') ?? '',
					},
					{
						title: t('understandingHeartfulness'),
						description: t('abilityToExplainHeartfulness') ?? '',
					},
					{
						title: t('dietaryHabitsAndHealth'),
						description: t('expectedToBeRoleModel') ?? '',
					},
				],
			},
		],
		[t]
	);
	const steps = wizards[currentWizard].steps;
	const descriptions = wizards[currentWizard].descriptions;
	const [stepStates, setStepStates] = useState<number[]>(() => {
		const draftIndex = 0;
		return new Array(wizards.length)
			.fill(0)
			.map((_, index) =>
				index === currentWizard ? Math.max(0, draftIndex) : 0
			);
	});

	const showModal = useCallback(async (): Promise<boolean> => {
		return Promise.resolve(true);
	}, []);

	const customHandlePrevious = useCallback(() => {
		if (stepStates[currentWizard] === 0) {
			navigateToPage(isViewable ? INTERVIEW_LANDING : APPLICANT_CONTEXT);
		}
	}, [stepStates, navigateToPage, isViewable]);

	const completedDraftIndex = application?.applicantInterviewDone
		? steps.length
		: 0;

	const handleEvaluateClick = useCallback(() => {
		navigateToPage(INTERVIEW_LANDING);
	}, [navigateToPage]);

	return (
		<div
			className={`flex flex-col h-full min-h-0 p-[8px] ${
				!isMobile ? 'w-fit my-0 mx-auto' : 'w-full my-0 mx-auto'
			}`}
		>
			<div className="relative flex justify-between items-center w-full">
				<div
					className="flex font-sans justify-center gap-1 mb-1 items-center cursor-pointer absolute left-0"
					onClick={handleEvaluateClick}
				>
					<Icon src={LeftArrow} width={20} height={20} />
					{!isMobile && (
						<Text className="font-bold text-sm text-primary-blue whitespace-nowrap">
							{t('backToEvaluate')}
						</Text>
					)}
				</div>
				<Text className="flex-grow mt-4 mb-6 font-sans font-bold text-sm text-center text-secondary-black px-10">
					{t('candidateInterview')}
					{isViewable && ` (${t('viewOnly')})`}
				</Text>
			</div>
			<WizardContainer
				onSubmitClick={
					isRoleAllowed ? () => void handleEvaluateClick() : onSubmitClick
				}
				onNextClick={showModal}
				onPreviousClick={customHandlePrevious}
				steps={steps}
				stepDescription={descriptions}
				stepOffset={stepOffset}
				initialStepState={stepStates[currentWizard] || 0}
				onStepStateChange={(newState: number) => {
					setStepStates((prev) => {
						const updated = [...prev];
						updated[currentWizard] = newState;
						return updated;
					});
				}}
				currentWizard={currentWizard}
				draftIndex={completedDraftIndex}
				completedDraftIndex={completedDraftIndex}
				isProposerAndViewable={isProposerAndViewable}
			/>
		</div>
	);
};

export const ApplicantInterviewWizardContainer = () => {
	return (
		<ApplicantInterviewProvider>
			<WizardProvider>
				<ApplicantInterviewWizard />
			</WizardProvider>
		</ApplicantInterviewProvider>
	);
};
